import {
  cancellationReasonSchema,
  type BoardCaregiver,
  type CancellationReason,
  type MatchResult,
  type ShiftOfferList,
  type ShiftPatternView,
  type ShiftPatternWriteResult,
  type VisitDetail,
} from '@homecare/contracts';
import { Button, EmptyState, Field, Notice, SelectField, Spinner, StatusPill } from '@homecare/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

import { ErrorNote } from '../../components/primitives';
import { useApi } from '../../lib/api-hooks';
import { useSession } from '../../lib/session';
import { instantText } from '../../lib/schedule-time';

/**
 * What can be done to a visit, from the drawer.
 *
 * One panel open at a time. Each action is the API's own route — Find and
 * Assign is PATCH through every rule, Call out and Cancel are their own
 * routes so an edit can never reach a state around the machine (exit
 * criterion 9) — and the drawer shows the engine's refusal in its words.
 */

type Panel = 'none' | 'find' | 'offer' | 'callout' | 'cancel' | 'pattern';

const CANCEL_WORDS: Record<CancellationReason, string> = {
  CLIENT_REQUEST: 'The client asked to cancel',
  CLIENT_HOSPITALISED: 'The client is in hospital',
  CLIENT_UNAVAILABLE: 'The client is not available',
  SERVICE_ENDED: 'The service has ended',
  AGENCY_ERROR: 'Booked in error',
  WEATHER: 'Weather',
  OTHER: 'Something else',
};

export function useScheduleRefresh(visitId: string): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['schedule'] }),
      queryClient.invalidateQueries({ queryKey: ['visits', visitId] }),
    ]);
  };
}

export function VisitActions({
  visit,
  timeZone,
  caregivers,
}: {
  visit: VisitDetail;
  timeZone: string;
  caregivers: readonly BoardCaregiver[];
}): ReactNode {
  const permissions = useSession((state) => state.permissions);
  const [panel, setPanel] = useState<Panel>('none');
  const canUpdate = permissions.includes('schedule:update');

  if (visit.status !== 'SCHEDULED') {
    return (
      <Notice tone="inactive" title="Nothing to change">
        A visit that is no longer scheduled is not edited from the board.
      </Notice>
    );
  }
  if (!canUpdate) {
    return (
      <Notice tone="inactive" title="You can look, not change">
        Changing a visit needs the schedule:update permission. An admin can grant it.
      </Notice>
    );
  }

  const isOpen = visit.caregiverId === null;
  const toggle = (next: Panel) => () => setPanel((was) => (was === next ? 'none' : next));

  return (
    <section aria-labelledby="visit-actions" className="grid gap-3">
      <h3 id="visit-actions" className="sr-only">
        Actions
      </h3>
      <div className="flex flex-wrap gap-2">
        <Button
          variant={isOpen ? 'primary' : 'secondary'}
          size="sm"
          onClick={toggle('find')}
          aria-expanded={panel === 'find'}
        >
          {isOpen ? 'Find a caregiver' : 'Change caregiver'}
        </Button>
        {isOpen && (
          <Button
            variant="secondary"
            size="sm"
            onClick={toggle('offer')}
            aria-expanded={panel === 'offer'}
          >
            Offer to caregivers
          </Button>
        )}
        {!isOpen && (
          <Button
            variant="secondary"
            size="sm"
            onClick={toggle('callout')}
            aria-expanded={panel === 'callout'}
          >
            Call out
          </Button>
        )}
        {visit.shiftPatternId && (
          <Button
            variant="secondary"
            size="sm"
            onClick={toggle('pattern')}
            aria-expanded={panel === 'pattern'}
          >
            Change the pattern
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={toggle('cancel')}
          aria-expanded={panel === 'cancel'}
        >
          Cancel visit
        </Button>
      </div>

      {panel === 'find' && <FindCaregiver visit={visit} onDone={() => setPanel('none')} />}
      {panel === 'offer' && <OfferPanel visit={visit} timeZone={timeZone} />}
      {panel === 'callout' && <CallOutForm visit={visit} onDone={() => setPanel('none')} />}
      {panel === 'cancel' && <CancelForm visit={visit} onDone={() => setPanel('none')} />}
      {panel === 'pattern' && visit.shiftPatternId && (
        <PatternEditor
          visit={visit}
          patternId={visit.shiftPatternId}
          caregivers={caregivers}
          onDone={() => setPanel('none')}
        />
      )}
      {isOpen && panel !== 'offer' && <OfferList visit={visit} timeZone={timeZone} />}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Find a caregiver — §14.4, ranked and explained
// ---------------------------------------------------------------------------

function FindCaregiver({ visit, onDone }: { visit: VisitDetail; onDone: () => void }): ReactNode {
  const api = useApi();
  const refresh = useScheduleRefresh(visit.id);
  const [assigning, setAssigning] = useState<string | null>(null);
  const [failure, setFailure] = useState<unknown>(null);

  const matches = useQuery({
    queryKey: ['visits', visit.id, 'matches'],
    queryFn: () => api.get<MatchResult>(`/v1/visits/${visit.id}/matches`),
    staleTime: 0,
  });

  const assign = async (caregiverId: string): Promise<void> => {
    setAssigning(caregiverId);
    setFailure(null);
    try {
      await api.send('PATCH', `/v1/visits/${visit.id}`, { version: visit.version, caregiverId });
      await refresh();
      onDone();
    } catch (error) {
      setFailure(error);
    } finally {
      setAssigning(null);
    }
  };

  const data = matches.data;

  return (
    <div
      className="border-line bg-surface-2 grid gap-3 rounded-md border p-4"
      data-testid="matches"
    >
      <ErrorNote
        error={failure}
        nextStep="Choose someone else, or reload the board and try again."
      />
      {matches.isPending ? (
        <p className="text-ink-2 flex items-center gap-2 text-sm" role="status">
          <Spinner /> Ranking caregivers against all fifteen rules…
        </p>
      ) : matches.isError ? (
        <ErrorNote error={matches.error} nextStep="Close the panel and open it again." />
      ) : data ? (
        <>
          {data.visitBlocks.length > 0 && (
            <Notice tone="critical" title="Nobody can take this visit as it stands">
              <ul className="grid gap-1">
                {data.visitBlocks.map((block) => (
                  <li key={block.code}>{block.message}</li>
                ))}
              </ul>
            </Notice>
          )}
          {data.ranked.length === 0 ? (
            <EmptyState
              title="Nobody passes every rule for this visit"
              explanation={`Within ${data.maxTravelMinutes} minutes' travel, no caregiver is free, credentialed and allowed. Who was ruled out, and why, is listed below.`}
            />
          ) : (
            <ol className="grid gap-2" aria-label="Caregivers, best match first">
              {data.ranked.map((candidate, at) => (
                <li
                  key={candidate.caregiverId}
                  className="border-line bg-surface grid gap-1 rounded-md border p-3"
                  data-testid="match"
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-ink font-semibold">
                      {at + 1}. {candidate.name}
                      <span className="text-ink-3 font-normal"> · score {candidate.score}</span>
                    </span>
                    <Button
                      size="sm"
                      pending={assigning === candidate.caregiverId}
                      onClick={() => void assign(candidate.caregiverId)}
                      aria-label={`Assign ${candidate.name}`}
                    >
                      Assign
                    </Button>
                  </div>
                  <p className="text-ink-2 text-sm">{candidate.explanation}</p>
                  {candidate.warnings.map((warning) => (
                    <p key={warning.code} className="text-warning text-xs">
                      {warning.message}
                    </p>
                  ))}
                </li>
              ))}
            </ol>
          )}
          {data.excluded.length + data.unrankable.length > 0 && (
            <details className="text-sm">
              <summary className="text-ink-2 cursor-pointer font-medium">
                {data.excluded.length} ruled out, {data.unrankable.length} could not be checked
              </summary>
              <ul className="mt-2 grid gap-1">
                {data.excluded.map((entry) => (
                  <li key={entry.caregiverId}>
                    <span className="text-ink font-medium">{entry.name}</span>
                    <span className="text-ink-3">
                      {' '}
                      — {entry.reasons.map((r) => r.message).join(' ')}
                    </span>
                  </li>
                ))}
                {data.unrankable.map((entry) => (
                  <li key={entry.caregiverId}>
                    <span className="text-ink font-medium">{entry.name}</span>
                    <span className="text-ink-3">
                      {' '}
                      — not checked: {entry.unknowns.map((r) => r.message).join(' ')}
                    </span>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Offer in waves — §14.5
// ---------------------------------------------------------------------------

const TIERS = [
  { value: 'TOP_5', label: 'The top 5' },
  { value: 'TOP_15', label: 'The top 15' },
  { value: 'ALL_ELIGIBLE', label: 'Everyone eligible' },
] as const;

function OfferPanel({ visit, timeZone }: { visit: VisitDetail; timeZone: string }): ReactNode {
  const api = useApi();
  const refresh = useScheduleRefresh(visit.id);
  const queryClient = useQueryClient();
  const [tier, setTier] = useState<(typeof TIERS)[number]['value']>('TOP_5');
  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);

  const send = async (): Promise<void> => {
    setSending(true);
    setFailure(null);
    try {
      await api.send('POST', `/v1/visits/${visit.id}/offers`, { tier });
      await queryClient.invalidateQueries({ queryKey: ['visits', visit.id, 'offers'] });
      await refresh();
    } catch (error) {
      setFailure(error);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="border-line bg-surface-2 grid gap-3 rounded-md border p-4">
      <ErrorNote error={failure} nextStep="Check the offers below, then try again." />
      <SelectField
        label="Offer to"
        hint="Offered in waves of five, best match first. The first to claim gets it."
        value={tier}
        onChange={(event) => setTier(event.target.value as typeof tier)}
      >
        {TIERS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </SelectField>
      <div>
        <Button size="sm" pending={sending} onClick={() => void send()}>
          Send the offer
        </Button>
      </div>
      <OfferList visit={visit} timeZone={timeZone} />
    </div>
  );
}

const OFFER_TONE = {
  PENDING: 'inactive',
  OFFERED: 'scheduled',
  VIEWED: 'progress',
  CLAIMED: 'verified',
  DECLINED: 'critical',
  EXPIRED: 'inactive',
  WITHDRAWN: 'inactive',
} as const;

function OfferList({ visit, timeZone }: { visit: VisitDetail; timeZone: string }): ReactNode {
  const api = useApi();
  const offers = useQuery({
    queryKey: ['visits', visit.id, 'offers'],
    queryFn: () => api.get<ShiftOfferList>(`/v1/visits/${visit.id}/offers`),
    staleTime: 0,
  });

  if (offers.isPending) return null;
  if (offers.isError)
    return <ErrorNote error={offers.error} nextStep="Reopen the visit to try again." />;
  const list = offers.data.offers;
  if (list.length === 0) return null;

  return (
    <div className="grid gap-1 text-sm" data-testid="offers">
      <h4 className="text-ink font-semibold">Offers sent</h4>
      <ul className="grid gap-1">
        {list.map((offer) => (
          <li key={offer.id} className="flex items-center justify-between gap-2">
            <span className="text-ink">
              Wave {offer.wave} · {offer.caregiverName}
              <span className="text-ink-3 text-xs">
                {' '}
                · until {instantText(offer.expiresAt, timeZone)}
              </span>
            </span>
            <StatusPill tone={OFFER_TONE[offer.state]}>{offer.state.toLowerCase()}</StatusPill>
          </li>
        ))}
      </ul>
      {offers.data.notReached > 0 && (
        <p className="text-ink-3 text-xs">
          {offers.data.notReached} more will be offered in later waves if nobody claims it.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Call out and cancel — their own routes, never an edit
// ---------------------------------------------------------------------------

function CallOutForm({ visit, onDone }: { visit: VisitDetail; onDone: () => void }): ReactNode {
  const api = useApi();
  const refresh = useScheduleRefresh(visit.id);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);

  const save = async (): Promise<void> => {
    setSaving(true);
    setFailure(null);
    try {
      await api.send('POST', `/v1/visits/${visit.id}/callout`, { version: visit.version, reason });
      await refresh();
      onDone();
    } catch (error) {
      setFailure(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border-line bg-surface-2 grid gap-3 rounded-md border p-4">
      <p className="text-ink-2 text-sm">
        {visit.caregiverName} comes off this visit. It stays booked, shows as called out, and the
        refill clock starts now.
      </p>
      <ErrorNote
        error={failure}
        nextStep="Reload the board — someone may have changed this visit."
      />
      <Field
        label="Why can’t they make it?"
        hint="It stays with the call-out."
        value={reason}
        onChange={(event) => setReason(event.target.value)}
      />
      <div>
        {reason.trim() ? (
          <Button size="sm" variant="danger" pending={saving} onClick={() => void save()}>
            Record the call-out
          </Button>
        ) : (
          <Button size="sm" variant="danger" disabled disabledReason="Say why first.">
            Record the call-out
          </Button>
        )}
      </div>
    </div>
  );
}

function CancelForm({ visit, onDone }: { visit: VisitDetail; onDone: () => void }): ReactNode {
  const api = useApi();
  const refresh = useScheduleRefresh(visit.id);
  const [reasonCode, setReasonCode] = useState<CancellationReason | ''>('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);

  const save = async (): Promise<void> => {
    setSaving(true);
    setFailure(null);
    try {
      await api.send('POST', `/v1/visits/${visit.id}/cancel`, {
        version: visit.version,
        reasonCode,
        note: note.trim() || null,
      });
      await refresh();
      onDone();
    } catch (error) {
      setFailure(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border-line bg-surface-2 grid gap-3 rounded-md border p-4">
      <ErrorNote
        error={failure}
        nextStep="Reload the board — someone may have changed this visit."
      />
      <SelectField
        label="Why is it cancelled?"
        hint="Inside 24 hours of the start, it is recorded as a late cancellation."
        value={reasonCode}
        onChange={(event) => setReasonCode(event.target.value as CancellationReason)}
      >
        <option value="">Choose a reason</option>
        {cancellationReasonSchema.options.map((code) => (
          <option key={code} value={code}>
            {CANCEL_WORDS[code]}
          </option>
        ))}
      </SelectField>
      <Field
        label="Note (optional)"
        value={note}
        onChange={(event) => setNote(event.target.value)}
      />
      <div>
        {reasonCode ? (
          <Button size="sm" variant="danger" pending={saving} onClick={() => void save()}>
            Cancel this visit
          </Button>
        ) : (
          <Button size="sm" variant="danger" disabled disabledReason="Choose a reason first.">
            Cancel this visit
          </Button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The pattern — this occurrence, this and future, all future
// ---------------------------------------------------------------------------

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const SCOPES = [
  { value: 'THIS_OCCURRENCE', label: 'Only this visit' },
  { value: 'THIS_AND_FUTURE', label: 'This visit and every one after it' },
  { value: 'ALL_FUTURE', label: 'Every visit from today on' },
] as const;

function PatternEditor({
  visit,
  patternId,
  caregivers,
  onDone,
}: {
  visit: VisitDetail;
  patternId: string;
  caregivers: readonly BoardCaregiver[];
  onDone: () => void;
}): ReactNode {
  const api = useApi();
  const refresh = useScheduleRefresh(visit.id);
  const pattern = useQuery({
    queryKey: ['shift-patterns', patternId],
    queryFn: () => api.get<ShiftPatternView>(`/v1/shift-patterns/${patternId}`),
  });

  const [scope, setScope] = useState<(typeof SCOPES)[number]['value']>('THIS_AND_FUTURE');
  const [startTime, setStartTime] = useState(visit.localStartTime);
  const [endTime, setEndTime] = useState(visit.localEndTime);
  const [caregiverId, setCaregiverId] = useState(visit.caregiverId ?? '');
  const [ending, setEnding] = useState(false);
  const [endReason, setEndReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  const [result, setResult] = useState<ShiftPatternWriteResult | null>(null);

  const changes = {
    ...(startTime !== visit.localStartTime ? { startTime } : {}),
    ...(endTime !== visit.localEndTime ? { endTime } : {}),
    ...(caregiverId !== (visit.caregiverId ?? '') ? { caregiverId: caregiverId || null } : {}),
  };
  const changed = Object.keys(changes).length > 0;

  const run = async (work: () => Promise<ShiftPatternWriteResult | null>): Promise<void> => {
    setSaving(true);
    setFailure(null);
    try {
      setResult(await work());
      await refresh();
    } catch (error) {
      setFailure(error);
    } finally {
      setSaving(false);
    }
  };

  const save = (): Promise<void> =>
    run(() =>
      api.send<ShiftPatternWriteResult>('POST', `/v1/shift-patterns/${patternId}/edit`, {
        scope,
        occurrenceDate: visit.localDate,
        changes,
      }),
    );

  const end = (): Promise<void> =>
    run(async () => {
      await api.send('POST', `/v1/shift-patterns/${patternId}/end`, {
        lastDate: visit.localDate,
        reason: endReason,
      });
      onDone();
      return null;
    });

  const rule = pattern.data;

  return (
    <div
      className="border-line bg-surface-2 grid gap-3 rounded-md border p-4"
      data-testid="pattern-editor"
    >
      {pattern.isPending ? (
        <p className="text-ink-2 flex items-center gap-2 text-sm" role="status">
          <Spinner /> Loading the pattern…
        </p>
      ) : rule ? (
        <p className="text-ink-2 text-sm">
          Repeats {rule.daysOfWeek.map((day) => DAY_NAMES[day]).join(', ')}, {rule.startTime}–
          {rule.endTime}
          {rule.weekInterval > 1 ? `, every ${rule.weekInterval} weeks` : ', every week'}, from{' '}
          {rule.effectiveFrom}
          {rule.effectiveTo ? ` to ${rule.effectiveTo}` : ''}. Visits already in the past never
          change.
        </p>
      ) : (
        <ErrorNote error={pattern.error} nextStep="Reopen the visit to try again." />
      )}

      <ErrorNote error={failure} nextStep="Change what the reason names, then save again." />
      {result && (
        <Notice tone="verified" title={`Saved. ${result.replacedVisits} visits replaced.`}>
          {result.materialisation.atRisk.length > 0
            ? `${result.materialisation.atRisk.length} of the new visits are at risk — they are flagged on the board.`
            : 'Every new visit passed the rules.'}
        </Notice>
      )}

      <fieldset className="grid gap-1">
        <legend className="text-ink-2 text-sm font-semibold">Apply the change to</legend>
        {SCOPES.map((option) => (
          <label key={option.value} className="text-ink flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="pattern-scope"
              value={option.value}
              checked={scope === option.value}
              onChange={() => setScope(option.value)}
            />
            {option.label}
          </label>
        ))}
      </fieldset>

      <div className="grid gap-3 sm:grid-cols-2">
        <Field
          label="Starts"
          type="time"
          value={startTime}
          onChange={(event) => setStartTime(event.target.value)}
        />
        <Field
          label="Ends"
          type="time"
          value={endTime}
          onChange={(event) => setEndTime(event.target.value)}
        />
      </div>
      <SelectField
        label="Caregiver"
        value={caregiverId}
        onChange={(event) => setCaregiverId(event.target.value)}
      >
        <option value="">Nobody — leave open</option>
        {caregivers.map((caregiver) => (
          <option key={caregiver.id} value={caregiver.id}>
            {caregiver.name}
          </option>
        ))}
      </SelectField>
      <div className="flex flex-wrap gap-2">
        {changed ? (
          <Button size="sm" pending={saving && !ending} onClick={() => void save()}>
            Save the change
          </Button>
        ) : (
          <Button size="sm" disabled disabledReason="Change a time or the caregiver first.">
            Save the change
          </Button>
        )}
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setEnding((was) => !was)}
          aria-expanded={ending}
        >
          End the pattern
        </Button>
      </div>
      {ending && (
        <div className="grid gap-2">
          <Field
            label={`Why does it end after ${visit.localDate}?`}
            value={endReason}
            onChange={(event) => setEndReason(event.target.value)}
          />
          <div>
            {endReason.trim() ? (
              <Button size="sm" variant="danger" pending={saving} onClick={() => void end()}>
                End it after this visit
              </Button>
            ) : (
              <Button size="sm" variant="danger" disabled disabledReason="Say why it is ending.">
                End it after this visit
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
